import * as React from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import TableRow from "@mui/material/TableRow";
import MenuItem from "@mui/material/MenuItem";
import { useTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import TableCell from "@mui/material/TableCell";
import SaveIcon from "@mui/icons-material/Save";
import InputLabel from "@mui/material/InputLabel";
import CloseIcon from "@mui/icons-material/Close";
import ButtonGroup from "@mui/material/ButtonGroup";
import FormControl from "@mui/material/FormControl";
import DeleteIcon from "@mui/icons-material/Delete";
import OutlinedInput from "@mui/material/OutlinedInput";

import { UserRoles } from "./user-roles.enum";
import { PasswordStatus } from "./password-status.enum";
import { columns, userToWarehouseAccessType } from "./common";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			width: 250,
		},
	},
};

function getStyles(theme) {
	return {
		fontWeight: theme.typography.fontWeightRegular,
	};
}

export default function EmployeeEditableTableRow({ row, warehouses, actions }) {
	const theme = useTheme();
	const rolesToSet = Object.values(UserRoles);
	const minorRolesToSet = Object.values(UserRoles).filter(
		(ur) => ur !== UserRoles.ADMIN
	);
	const editablePassword = row.passwordStatus === PasswordStatus.TEMPORARY;
	const [name, setName] = React.useState(row.name);
	const [friendlyName, setFriendlyName] = React.useState(row.friendlyName);
	const [role, setRole] = React.useState(row.role ? row.role : "");
	const [minorRoles, setMinorRoles] = React.useState(row.minorRoles);
	const [managerId, setManagerId] = React.useState(row.emloyee1c);
	const [email, setEmail] = React.useState(row.email);
	const [readWarehouseIds, setReadWarehouseIds] = React.useState(
		row.userToWarehouses
			? row.userToWarehouses
					.filter((utw) => utw.accessType === userToWarehouseAccessType.READ)
					.map((utw) => utw.warehouse.id)
			: []
	);
	const [writeWarehouseIds, setWriteWarehouseIds] = React.useState(
		row.userToWarehouses
			? row.userToWarehouses
					.filter((utw) => utw.accessType === userToWarehouseAccessType.WRITE)
					.map((utw) => utw.warehouse.id)
			: []
	);
	const [tempPassword, setTempPassword] = React.useState(row.tempPassword);

	const handleSaveRowClick = async () => {
		actions.saveRow(
			{
				id: row.id,
				name,
				friendlyName,
				role,
				email,
				minorRoles,
				tempPassword,
				emloyee1c: managerId,
			},
			{
				readWarehouses: readWarehouseIds,
				writeWarehouses: writeWarehouseIds,
			}
		);
	};

	const handleCancelEditRowClick = () => {
		actions.cancelEdit(row.id);
	};

	const handleDeleteRowClick = () => {
		actions.tryDeleteRow(row.id);
	};

	const textInput = ({ value, setValue, disabled = false }) => (
		<TextField
			inputRef={(input) => input}
			sx={{
				width: "16ch",
				"& .Mui-focused": {
					width: "22ch",
				},
				"& .MuiInputBase-input": {
					width: "100%",
					height: 0,
				},
			}}
			variant="outlined"
			onChange={(event) => setValue(event.target.value)}
			value={value}
			disabled={disabled}
			inputProps={{ "aria-label": "value" }}
		/>
	);
	return (
		<>
			<TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
				{columns.map((column) => (
					<TableCell key={column.id} align={column.align}>
						{column.id === "buttons" && (
							<div>
								<div style={{ display: "flex", gap: 15 }}>
									<ButtonGroup
										variant="outlined"
										aria-label="outlined button group"
									>
										<Button
											onClick={() => handleSaveRowClick()}
											disabled={
												!name || !email || !role || tempPassword?.length < 6
											}
										>
											<SaveIcon />
										</Button>
										<Button
											color="error"
											onClick={() => handleDeleteRowClick()}
											disabled={row.id === "new-user-id"}
										>
											<DeleteIcon />
										</Button>
										<Button
											color="error"
											onClick={() => handleCancelEditRowClick()}
										>
											<CloseIcon />
										</Button>
									</ButtonGroup>
								</div>
							</div>
						)}
						{column.id === "name" &&
							textInput({ value: name, setValue: setName })}
						{column.id === "friendlyName" &&
							textInput({ value: friendlyName, setValue: setFriendlyName })}
						{column.id === "role" && (
							<FormControl sx={{ m: 1, minWidth: 120 }} size="small">
								<InputLabel>Роль</InputLabel>
								<Select
									value={role}
									label="Роль"
									onChange={(event) => {
										console.log("event", event.target.value);
										setRole(event.target.value);
									}}
								>
									{rolesToSet.map((roleItem) => (
										<MenuItem key={roleItem} value={roleItem}>
											{roleItem}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						)}
						{column.id === "minorRoles" && (
							<FormControl sx={{ m: 1, width: 250 }}>
								<InputLabel id="minor-roles-select-label">Ролі</InputLabel>
								<Select
									labelId="minor-roles-select-label"
									id="minor-roles-select"
									multiple={true}
									value={minorRoles}
									onChange={(event) => {
										const {
											target: { value },
										} = event;
										console.log("setMinorRolesValue", value);
										setMinorRoles(() => value);
									}}
									input={
										<OutlinedInput
											id="select-multiple-chip"
											label="Ролі"
											value=""
										/>
									}
									renderValue={(selected) => (
										<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
											{selected.map((value) => (
												<Chip key={value} label={value} />
											))}
										</Box>
									)}
								>
									{minorRolesToSet.map((r) => (
										<MenuItem key={r} value={r}>
											{r}
											{/* {r.city}&nbsp; - &nbsp;{r.name} */}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						)}
						{column.id === "managerId" &&
							textInput({ value: managerId, setValue: setManagerId })}
						{column.id === "email" &&
							textInput({ value: email, setValue: setEmail })}

						{column.id === "readWarehouses" && (
							<FormControl sx={{ m: 1, width: 250 }}>
								<InputLabel id="demo-multiple-chip-label">Склади</InputLabel>
								<Select
									labelId="demo-multiple-chip-label"
									id="demo-multiple-chip"
									multiple={true}
									value={readWarehouseIds}
									onChange={(event) => {
										const {
											target: { value },
										} = event;
										setReadWarehouseIds(() => value);
										setWriteWarehouseIds((old) =>
											old.filter((x) => value.includes(x))
										);
									}}
									input={
										<OutlinedInput
											id="select-multiple-chip"
											label="Склади"
											value=""
										/>
									}
									renderValue={(selected) => (
										<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
											{selected.map((value) => (
												<Chip
													key={value}
													label={warehouses.find((w) => w.id === value).city}
												/>
											))}
										</Box>
									)}
									// MenuProps={MenuProps}
								>
									{warehouses.map((w) => (
										<MenuItem
											key={w.id}
											value={w.id}
											// style={getStyles(theme)}
										>
											{w.city}&nbsp; - &nbsp;{w.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						)}
						{column.id === "writeWarehouses" && (
							<FormControl sx={{ m: 1, width: 250 }}>
								<InputLabel id="demo-multiple-chip-label">Склади</InputLabel>
								<Select
									labelId="demo-multiple-chip-label"
									id="demo-multiple-chip"
									multiple={true}
									value={writeWarehouseIds}
									onChange={(event) => {
										const {
											target: { value },
										} = event;
										setWriteWarehouseIds(() => value);
									}}
									input={
										<OutlinedInput
											id="select-multiple-chip"
											label="Склади"
											value=""
										/>
									}
									renderValue={(selected) => (
										<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
											{selected.map((value) => (
												<Chip
													key={value}
													label={warehouses.find((w) => w.id === value).city}
												/>
											))}
										</Box>
									)}
									// MenuProps={MenuProps}
								>
									{warehouses
										.filter((w) => readWarehouseIds.includes(w.id))
										.map((w) => (
											<MenuItem
												key={w.id}
												value={w.id}
												// style={getStyles(theme)}
											>
												{w.city}&nbsp; - &nbsp;{w.name}
											</MenuItem>
										))}
								</Select>
							</FormControl>
						)}
						{column.id === "tempPassword" &&
							(editablePassword
								? textInput({
										value: tempPassword,
										setValue: setTempPassword,
								  })
								: "Користувач змінив пароль")}
					</TableCell>
				))}
			</TableRow>
		</>
	);
}
